import { useCallback, useState } from "react";
import { getDataForCycleItem } from "../api/CycleCountGroups";
import { useCycleCountStore } from "../store/cycle-count";
import {
  CountEntryCard,
  CycleCountResults,
} from "../models/materialissue/MaterialIssue";
import { getColumnsWithGroup } from "../api/Windows";
import { getOptionByIdWithCORP } from "../api/TabwareOptions";
import { getInventoryControl } from "../api/PlantSetups";

/**
 * Default number of count items downloaded for offline access.
 * Overridable (downwards) per device via the settings store (Settings screen),
 * but never above MAX_CYCLE_COUNT_ITEM_LIMIT to protect device storage.
 */
export const DEFAULT_CYCLE_COUNT_ITEM_LIMIT = 200;

/**
 * Hard upper bound on the offline item-download limit. The user cannot exceed
 * this regardless of what they type in Settings (a large value could blow past
 * the device's storage capacity).
 */
export const MAX_CYCLE_COUNT_ITEM_LIMIT = 200;

export type DownloadCycleCountsResult = {
  downloadedBatchIds: number[];
  // batches skipped because they would exceed the item-download limit
  skippedBatchIds: number[];
  itemsDownloaded: number;
  limitReached: boolean;
};

/**
 * Download the selected cycle count batches and their count items, and cache
 * them for offline access. Mirrors useDownloadWorkOrdersForOfflineAccess, but
 * cycle count has a much smaller related-data graph (just the count items per
 * batch), so the flow is a single fetch per batch.
 */
export default function useDownloadCycleCountsForOfflineAccess() {
  const [isDownloading, setIsDownloading] = useState(false);

  const setDownloadedBatches = useCycleCountStore(
    (state) => state.setDownloadedBatches
  );
  const setCountEntriesForBatch = useCycleCountStore(
    (state) => state.setCountEntriesForBatch
  );
  const setMeta = useCycleCountStore((state) => state.setMeta);

  // Fetch (once) the global metadata/options the entry screen needs to render
  // and validate counts offline.
  const downloadMeta = useCallback(async () => {
    // allSettled so a single failing metadata call can't reject the whole
    // download (which would leave the caller's loader spinning forever)
    const [cardColumns, scanColumns, scanConfig, variance, inventoryControl] =
      await Promise.allSettled([
        getColumnsWithGroup({ WindowName: "mob_inventory_cyclecount" }),
        getColumnsWithGroup({ WindowName: "mob_inventory_cyclecount_scan" }),
        getOptionByIdWithCORP("CycleCountingScannerConfiguration"),
        getOptionByIdWithCORP("AllowedCycleCountVariance"),
        getInventoryControl(),
      ]);
    const val = (r: PromiseSettledResult<any>) =>
      r.status === "fulfilled" ? r.value : undefined;
    const scanConfigData = val(scanConfig)?.data;
    const varianceData = val(variance)?.data;
    setMeta({
      cardColumns: val(cardColumns)?.data,
      scanColumns: val(scanColumns)?.data,
      scanOption:
        scanConfigData?.PlantValue || scanConfigData?.DefaultValue || "Item",
      allowedVariance: varianceData?.PlantValue ?? varianceData?.DefaultValue,
      adjustQtyCommentFlag: val(inventoryControl)?.data?.AdjustQtyCommentFlag,
    });
  }, [setMeta]);

  const trigger = useCallback(
    async (
      batches: CycleCountResults[],
      itemLimit: number = DEFAULT_CYCLE_COUNT_ITEM_LIMIT
    ): Promise<DownloadCycleCountsResult> => {
      const result: DownloadCycleCountsResult = {
        downloadedBatchIds: [],
        skippedBatchIds: [],
        itemsDownloaded: 0,
        limitReached: false,
      };
      if (!batches.length) return result;

      // hard-cap the limit regardless of caller/stored value (storage safety)
      itemLimit = Math.min(itemLimit, MAX_CYCLE_COUNT_ITEM_LIMIT);

      setIsDownloading(true);
      const downloadedBatches: CycleCountResults[] = [];
      try {
        for (const batch of batches) {
          const batchId = batch.batch_id_0;
          const response = await getDataForCycleItem({ batchId });
          if (response.isError) {
            result.skippedBatchIds.push(batchId);
            continue;
          }
          const entries = (response.data?.data ?? []) as CountEntryCard[];

          // Enforce the limit at batch granularity: only take a whole batch if
          // it fits in the remaining budget. A partially-downloaded batch would
          // present a wrong/incomplete item list offline, so we never split one.
          // If a single batch on its own exceeds the limit we still download it
          // (the count can't be performed otherwise) but flag limitReached.
          const remaining = itemLimit - result.itemsDownloaded;
          const isFirstBatch = downloadedBatches.length === 0;
          if (entries.length > remaining && !isFirstBatch) {
            result.skippedBatchIds.push(batchId);
            result.limitReached = true;
            continue;
          }

          setCountEntriesForBatch(batchId, entries);
          downloadedBatches.push(batch);
          result.downloadedBatchIds.push(batchId);
          result.itemsDownloaded += entries.length;
          if (result.itemsDownloaded >= itemLimit) {
            result.limitReached = true;
          }
        }
        setDownloadedBatches(downloadedBatches);
        // cache the entry-screen metadata once for all downloaded batches
        await downloadMeta();
      } finally {
        setIsDownloading(false);
      }
      return result;
    },
    [downloadMeta, setCountEntriesForBatch, setDownloadedBatches]
  );

  return { trigger, isDownloading };
}
